import type { LabResultCategoryKey } from '@liza/fhir-utils';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { LabResultCategoryDetail } from '@liza/ui/components/records/LabResultCategoryDetail';
import { defaultFilters } from '@liza/ui/components/records/RecordsListControls';
import { useTranslation } from 'react-i18next';
import { useLabResultRecords } from '../RecordDetails/useLabResultRecords';

type Props = {
  labGroup: LabResultCategoryKey;
  medplumPatientId: string;
  /** Open one lab's detail, from a row's header. */
  onSelectLab: (code: string) => void;
};

/**
 * Level 2 of the labs view: one category charted as the care plan's detailed Results stack.
 *
 * Runs the same query as the list with default filters, so an active search or ownership filter in
 * the list column does not narrow what the chart shows.
 */
export const LabGroupDetailContent = ({ labGroup, medplumPatientId, onSelectLab }: Props): React.ReactNode => {
  const { t } = useTranslation('common');
  const { categories, query, isComplete } = useLabResultRecords(medplumPatientId, defaultFilters);
  const category = categories.find((c) => c.key === labGroup);

  return (
    <RecordListScroll isLoading={query.isLoading} isError={query.isError} error={query.error}>
      {category ? (
        <div className="mx-auto w-full max-w-4xl p-5" data-testid="lab-group-detail">
          <LabResultCategoryDetail category={category} onSelectLab={onSelectLab} />
        </div>
      ) : (
        // Every curated category is always present, so the only one that can be missing is `other`,
        // and only until a page carrying an uncurated result has landed.
        isComplete && <p className="p-4">{t('noRecordsAvailable')}</p>
      )}
    </RecordListScroll>
  );
};
